import React, { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { Styles } from 'jss';
import mapValues from 'lodash/mapValues';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Colors, Theme } from '@app/theme';
import {
  ActionCausedBy,
  ActionCausedByRole,
  useAccountById,
  useIsAdmin,
} from '@app/api';
import { useCurrentOrganizationMembers } from 'queries';

const useStyles = createUseStyles((theme: Theme) => ({
  causedBy: {
    ...theme.fonts.label,
    whiteSpace: 'nowrap',
  },
}));

const useColorsStyle = createUseStyles((theme: Theme): Styles<Colors> => {
  return mapValues(theme.colorDefinitions, (def) => ({
    color: def.accessible,
  }));
});

interface Props {
  causedBy: ActionCausedBy;
  color: Colors;
  anonymousCausedBy?: ActionCausedByRole;
}

export const EventCausedByCell = ({
  causedBy,
  color,
  anonymousCausedBy,
}: Props) => {
  const classes = useStyles();
  const textColorClasses = useColorsStyle();

  const { t } = useTranslation();

  const isAdmin = useIsAdmin();
  const { data: account } = useAccountById(causedBy.accountId, {
    retry: false,
    enabled: isAdmin && !!causedBy.accountId,
  });
  const { data: members } = useCurrentOrganizationMembers();

  const causedByName = useMemo(() => {
    if (account) {
      return account.firstName;
    }

    if (
      causedBy.accountId &&
      members &&
      causedBy.role === ActionCausedByRole.CLIENT
    ) {
      const client = members.find(
        (client) => client?.account.id === causedBy.accountId,
      );
      if (client) {
        return client.account?.firstName;
      }
    }

    let role = causedBy.role;
    if (role === ActionCausedByRole.ANONYMOUS_USER && anonymousCausedBy) {
      role = anonymousCausedBy;
    }

    return t(`eventHistory.roles.${role}`);
  }, [causedBy, account, members, anonymousCausedBy, t]);

  return (
    <div className={classNames(classes.causedBy, textColorClasses[color])}>
      {causedByName}
    </div>
  );
};
